import { LocalSessionKey } from 'app/constants/app-constants';
import { useCallback, useEffect, useState } from 'react';
import { LocalStorage } from 'app/utils/storage';
import {
  getCustomerPrivilege,
  updateCustomerPrivilege,
} from 'app/api/customer';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';

const dataPerPage = 10;

// Privileges are an Influencer-level perk. For a customer who was deleted and
// restored, the tab splits into:
//   - "Account restored" (bucket=after): the Influencer catalog, collectable,
//     with counts reset to the current Influencer cycle.
//   - "Account deleted" (bucket=before): read-only history of what was
//     collected before deletion.
// A never-deleted customer keeps the original flat list.
const useCustomerPrivilege = (latestDeletionDate) => {
  const customerData = JSON.parse(
    LocalStorage.get(LocalSessionKey.CUSTOMER_DATA)
  );

  const [pageLoading, setPageLoading] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [values, setValues] = useState({
    phoneNumber: '',
    mallId: '',
    privilegeId: '',
  });

  const [flatData, setFlatData] = useState([]);
  const [flatTotal, setFlatTotal] = useState(0);
  const [flatPageCount, setFlatPageCount] = useState(1);

  const [afterData, setAfterData] = useState([]);
  const [beforeData, setBeforeData] = useState([]);

  const fetchSection = useCallback(
    async (page, bucket) => {
      setPageLoading(true);
      try {
        const response = await getCustomerPrivilege(
          customerData?.id,
          page,
          dataPerPage,
          bucket
        );
        if (response?.responseCode === 200) {
          const rows = response?.data?.list || [];
          const count = response?.data?.count || 0;
          if (bucket === 'before') {
            setBeforeData(rows);
          } else if (bucket === 'after') {
            setAfterData(rows);
          } else {
            setFlatData(rows);
            setFlatTotal(count);
            setFlatPageCount(Math.ceil(count / dataPerPage) || 1);
          }
        } else {
          Toast.error(
            response?.response?.message || Message.Error.COMMON_MESSAGE
          );
        }
      } catch (error) {
        Toast.error(Message.Error.COMMON_MESSAGE);
      } finally {
        setPageLoading(false);
      }
    },
    [customerData?.id]
  );

  const refresh = useCallback(() => {
    if (!latestDeletionDate) {
      fetchSection(1, null);
      return;
    }
    fetchSection(1, 'after');
    fetchSection(1, 'before');
  }, [latestDeletionDate, fetchSection]);

  // Stable reference — PaginatedTable calls fetchData in a mount effect, so an
  // inline arrow here would re-fire it every render and loop forever.
  const fetchFlat = useCallback((page) => fetchSection(page, null), [
    fetchSection,
  ]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  function handleNew(data) {
    setValues((prev) => ({
      ...prev,
      phoneNumber: customerData?.phone,
      privilegeId: data?.id,
      privilege: data?.title,
      mallId: data?.mallId,
    }));
    setIsFilterOpen(true);
  }

  async function handleSubmit() {
    setPageLoading(true);
    try {
      const response = await updateCustomerPrivilege(values);
      if (response?.responseCode === 200) {
        Toast.success(response?.message);
        refresh();
      } else {
        Toast.error(response?.response?.message || Message.Error.COMMON_MESSAGE);
      }
    } catch (error) {
      Toast.error(Message.Error.COMMON_MESSAGE);
    } finally {
      setIsFilterOpen(false);
      setPageLoading(false);
    }
  }

  return {
    pageLoading,
    dataPerPage,

    // flat (never-deleted)
    flatData,
    flatTotal,
    flatPageCount,
    fetchFlat,

    // buckets (deleted/restored)
    afterData,
    beforeData,

    handleNew,
    isFilterOpen,
    setIsFilterOpen,
    values,
    setValues,
    handleSubmit,
  };
};

export default useCustomerPrivilege;
